import React, { useCallback, useMemo, useState } from 'react';

import { useQuery } from '@tanstack/react-query';
import { Spinner, Title } from '@skillarea/lib-frontend';
import {
  formatMoney,
  GamesLeaderboardEntryDto,
  IBaseArrayResultDto,
  StreakLeaderboardEntryDto,
  TLeaderboardPeriod,
  WinningsLeaderboardEntryDto,
} from '@skillarea/lib-shared';
import { useTranslation } from 'react-i18next';

import cx from 'classnames';

import { LayoutCard } from '../LayoutCard/LayoutCard';

import styles from './Leaderboard.module.scss';
import { LeaderboardTable } from './LeaderboardTable';

import { MatchService } from '~/services/matchService';
import { useUserId } from '~/stores/userStore';
import { PeriodSelector } from '~/components/PeriodSelector/PeriodSelector';

export const Leaderboard: React.FC = () => {
  const { t } = useTranslation('dashboard');
  const MAX_RENDERED_ROWS = 10;
  const userId = useUserId();
  const [period, setPeriod] = useState<TLeaderboardPeriod>('week');
  const matchService = useMemo(() => new MatchService(), []);

  const periodOptions = useMemo(
    () => [
      { value: 'today' as const, label: t('leaderboard.period.today') },
      { value: 'week' as const, label: t('leaderboard.period.week') },
      { value: 'month' as const, label: t('leaderboard.period.month') },
      { value: 'all' as const, label: t('leaderboard.period.all') },
    ],
    [t],
  );

  const visiblePeriodOptions = useMemo(
    () => periodOptions.filter((option) => option.value !== 'today'),
    [periodOptions],
  );

  const normalizeLeaderboard = useCallback(
    <T,>(response: IBaseArrayResultDto<T> | T[]): T[] => {
      if (Array.isArray(response)) return response;
      return response?.data || [];
    },
    [],
  );

  const getPositionDisplays = useCallback(
    <T extends { position?: number | null }>(
      rows: T[],
    ): (number | null | undefined | string)[] => {
      const seenPositions = new Set<number>();
      return rows.map((row) => {
        if (typeof row.position !== 'number') {
          return row.position;
        }

        if (seenPositions.has(row.position)) {
          return '';
        }

        seenPositions.add(row.position);
        return row.position;
      });
    },
    [],
  );

  const gamesQuery = useQuery({
    queryKey: ['leaderboard', 'games', period],
    queryFn: async () =>
      normalizeLeaderboard<GamesLeaderboardEntryDto>(
        await matchService.getGamesLeaderboard({ period }),
      ),
  });

  const winningsQuery = useQuery({
    queryKey: ['leaderboard', 'winnings', period],
    queryFn: async () =>
      normalizeLeaderboard<WinningsLeaderboardEntryDto>(
        await matchService.getWinningsLeaderboard({ period }),
      ),
  });

  const streakQuery = useQuery({
    queryKey: ['leaderboard', 'streaks', period],
    queryFn: async () =>
      normalizeLeaderboard<StreakLeaderboardEntryDto>(
        await matchService.getStreakLeaderboard({ period }),
      ),
  });

  const gamesEntries = (gamesQuery.data || []).slice(0, MAX_RENDERED_ROWS);
  const winningsEntries = (winningsQuery.data || []).slice(
    0,
    MAX_RENDERED_ROWS,
  );
  const streakEntries = (streakQuery.data || []).slice(0, MAX_RENDERED_ROWS);

  const gamesPositionDisplays = useMemo(
    () => getPositionDisplays(gamesEntries),
    [gamesEntries, getPositionDisplays],
  );

  const winningsPositionDisplays = useMemo(
    () => getPositionDisplays(winningsEntries),
    [winningsEntries, getPositionDisplays],
  );

  const streakPositionDisplays = useMemo(
    () => getPositionDisplays(streakEntries),
    [streakEntries, getPositionDisplays],
  );

  const handlePeriodChange = useCallback((value: TLeaderboardPeriod) => {
    setPeriod(value);
  }, []);

  return (
    <section className={styles.leaderboardsSection}>
      <div className={styles.leaderboardsHeader}>
        <Title
          className={cx(styles.leaderboardsTitle, styles.leaderboardsTitleMod)}
          color="contrast"
          size="l"
          variant="benzin"
        >
          {t('leaderboard.title')}
        </Title>
        <PeriodSelector
          onChange={handlePeriodChange}
          options={visiblePeriodOptions}
          value={period}
        />
      </div>

      <div className={styles.leaderboardsGrid}>
        <LayoutCard
          title={t('leaderboard.tables.chestsOpened.title')}
          titleClassName={styles.leaderboardTitle}
        >
          <div className={styles.tableWrapper}>
            {streakQuery.isLoading ? (
              <div className={styles.loader}>
                <Spinner />
              </div>
            ) : (
              <LeaderboardTable
                emptyLabel={t('leaderboard.tables.chestsOpened.empty')}
                headers={[
                  '',
                  t('leaderboard.tables.chestsOpened.name'),
                  t('leaderboard.tables.chestsOpened.days'),
                ]}
                rows={streakEntries.map((entry, index) => {
                  const positionDisplay = streakPositionDisplays[index];
                  const displayValue =
                    entry.streak === 0
                      ? '-'
                      : typeof positionDisplay === 'number'
                        ? positionDisplay
                        : (positionDisplay ?? '-');

                  return {
                    key: `${entry.userId}-${entry.position ?? 'na'}`,
                    isUserRow: entry.userId === userId,
                    cells: [displayValue, entry.name, entry.streak] as const,
                  };
                })}
              />
            )}
          </div>
        </LayoutCard>
        <LayoutCard
          title={t('leaderboard.tables.gamesPlayed.title')}
          titleClassName={styles.leaderboardTitle}
        >
          <div className={styles.tableWrapper}>
            {gamesQuery.isLoading ? (
              <div className={styles.loader}>
                <Spinner />
              </div>
            ) : (
              <LeaderboardTable
                emptyLabel={t('leaderboard.tables.gamesPlayed.empty')}
                headers={[
                  '',
                  t('leaderboard.tables.gamesPlayed.name'),
                  t('leaderboard.tables.gamesPlayed.games'),
                ]}
                rows={gamesEntries.map((entry, index) => {
                  const positionDisplay = gamesPositionDisplays[index];
                  const displayValue =
                    entry.games === 0
                      ? '-'
                      : typeof positionDisplay === 'number'
                        ? positionDisplay
                        : (positionDisplay ?? '-');

                  return {
                    key: `${entry.userId}-${entry.position ?? 'na'}`,
                    isUserRow: entry.userId === userId,
                    cells: [displayValue, entry.name, entry.games] as const,
                  };
                })}
              />
            )}
          </div>
        </LayoutCard>

        <LayoutCard
          title={t('leaderboard.tables.prizeWon.title')}
          titleClassName={styles.leaderboardTitle}
        >
          <div className={styles.tableWrapper}>
            {winningsQuery.isLoading ? (
              <div className={styles.loader}>
                <Spinner />
              </div>
            ) : (
              <LeaderboardTable
                emptyLabel={t('leaderboard.tables.prizeWon.empty')}
                headers={[
                  '',
                  t('leaderboard.tables.prizeWon.name'),
                  t('leaderboard.tables.prizeWon.prize'),
                ]}
                rows={winningsEntries.map((entry, index) => {
                  const positionDisplay = winningsPositionDisplays[index];
                  const displayValue =
                    entry.winnings === 0
                      ? '-'
                      : typeof positionDisplay === 'number'
                        ? positionDisplay
                        : (positionDisplay ?? '-');

                  return {
                    key: `${entry.userId}-${entry.position ?? 'na'}`,
                    isUserRow: entry.userId === userId,
                    cells: [
                      displayValue,
                      entry.name,
                      formatMoney(entry.winnings),
                    ] as const,
                  };
                })}
              />
            )}
          </div>
        </LayoutCard>
      </div>
    </section>
  );
};
